import './DashboardBase.scss';
import React from 'react'
import { DashboardSectionType } from '../../../../Model/Enum/DashboardType';
import { ChartType, getFriendlyNameForChartType } from '../../../../Model/Enum/ChartType';
import { IDashboardConfig } from '../../../../../State/dashboard/dashboardConfig';
import { defaultObject } from '../../../../../Helpers/typescriptHelper';
import { LocalStorageKey } from '../../../Constants/LocalStorageKey';
import * as cachingHelper from '../../../../../Helpers/Caching/cachingHelper';
import { DashboardService } from '../../../../Api/EmploymentEquity/Dashboard/DashboardService';
import { AddNewChart, IDropDownOption } from './addNewChart';
import { getChartSuffix } from '../../../../../Helpers/Chart/chartDropDownTextHelper';
import { IChartComponentData } from './IChartComponentData';
import { getChartColumnWidth } from '../../../../../Helpers/Chart/chartColumnWidthHelper';
import { ChartFactory } from '../../../../../Helpers/Factory/ChartFactory';
import { SmallLoading } from '../../Loading/IntermediateLoading';
import classNames from 'classnames';
import DeleteIcon from '@mui/icons-material/Delete';
import { MDBox } from '../../MDBox';
import { IconButton } from '@mui/material';
import { MDTypography } from '../../MDTypography';


interface IProps {
  heading: string;
  isVisible: boolean;
  scorecardId: string;
  scorecardTypeId?: string;
  scorecardYear?: number;
  dashboardSectionType: DashboardSectionType;
  availableChartElements: Array<ChartType>;
  chartOptions?: any;
  sourceScorecardIds?: string[];
  onClick?: (chartType: ChartType, chartLabel: string, occupationalLevelIds?: number[]) => void;
  isEEPlanTrackingYear5?: boolean;
}

export const DashboardBase = (props: IProps) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [selectedChartElementsState, setSelectedChartElements] = React.useState<Array<ChartType>>(props.availableChartElements);
  const [dashboardConfig, setDashboardConfig] = React.useState<IDashboardConfig>(defaultObject);

  const getDashboardConfigKey = (scorecardId: string): string => {
    const localStorageKey = `${LocalStorageKey.DashboardConfig}-${scorecardId}`;
    return localStorageKey;
  }

  const getSelectedChartsFromConfig = (config: IDashboardConfig) => {
    const dashboardSection = config[props.dashboardSectionType] ?? defaultObject;
    const selectedCharts = dashboardSection?.selectedCharts ?? [...props.availableChartElements,];
    return selectedCharts;
  }

  const getUpdatedConfig = (dashboardConfig: IDashboardConfig, selectedChartElements: Array<ChartType>, dashboardSectionType: DashboardSectionType
  ): string => {
    const updatedChartConfigFromState: IDashboardConfig = {
      ...dashboardConfig,
      [dashboardSectionType]: {
        selectedCharts: selectedChartElements,
      },
    };

    return JSON.stringify(updatedChartConfigFromState);
  };

  const saveConfigToLocalStorage = (dashboardConfig: IDashboardConfig, selectedChartElements: Array<ChartType>) => {
    const configString = getUpdatedConfig(dashboardConfig, selectedChartElements, props.dashboardSectionType);
    const ket = getDashboardConfigKey(props.scorecardId);
    cachingHelper.setCache(ket, configString, 600);
  }

  const getAvailableChartsDropDownOptions = (availableChartElements: Array<ChartType>, selectedChartElements: Array<ChartType>): Array<IDropDownOption> => {
    let dropDownOptions: Array<IDropDownOption> = [];
    if (availableChartElements !== null) {
      for (const available of availableChartElements) {
        if (selectedChartElements.includes(available)) continue;
        let label = getFriendlyNameForChartType(available, props.scorecardTypeId, props.scorecardYear);
        label += getChartSuffix(available);
        dropDownOptions.push({
          key: available.toString(),
          label: label,
        });
      }
    }
    return dropDownOptions;
  }

  const getChartsToDisplay = (isEditing: boolean, availableChartElements: Array<ChartType>, selectedChartElements: Array<ChartType>): Array<ChartType> => {
    const chartsToDisplay = [
      ...selectedChartElements.filter((s) =>
        props.availableChartElements.includes(s)
      ),
    ];
    if (
      (isEditing || selectedChartElements.length < 1) &&
      availableChartElements.length !== chartsToDisplay.length
    ) {
      chartsToDisplay.push(ChartType.AddNew);
    }
    return chartsToDisplay.sort((x, y) => x - y);
  }
  const addChart = (newItem: ChartType) => {
    const newSelectedChartElements = [...selectedChartElementsState, newItem];
    saveConfigToLocalStorage(dashboardConfig, newSelectedChartElements);
    setSelectedChartElements(newSelectedChartElements);
  }

  const deleteChart = (oldItem: ChartType) => {
    const chartIndex = selectedChartElementsState.findIndex((s) => s === oldItem);
    if (chartIndex < 0) return null;
    const newSelectedCharts = [
      ...selectedChartElementsState.slice(0, chartIndex),
      ...selectedChartElementsState.slice(chartIndex + 1),
    ];
    saveConfigToLocalStorage(dashboardConfig, newSelectedCharts);
    setSelectedChartElements(newSelectedCharts);

  }

  const renderCharts = (localProps: IProps): Array<Array<IChartComponentData>> => {
    let columnWidthTotalForRow: number = 0;
    let groupCount: number = 0;
    const chartListGroups: Array<Array<IChartComponentData>> = [[]];

    const {
      chartOptions,
      scorecardId,
      scorecardTypeId,
      availableChartElements,
      sourceScorecardIds
    } = localProps;

    const dropDownOptions = getAvailableChartsDropDownOptions(
      availableChartElements,
      selectedChartElementsState
    );

    const chartsToDisplay = getChartsToDisplay(
      isEditing,
      availableChartElements,
      selectedChartElementsState
    );
    for (const chartType of chartsToDisplay) {
      const columnWidth = getChartColumnWidth(chartType, props?.isEEPlanTrackingYear5 ?? false);

      const component =
        chartType === ChartType.AddNew ? (
          <AddNewChart
            dropdownOptions={dropDownOptions}
            onDropdownSelect={addChart}
          />
        ) : (
          <ChartFactory
            chartType={chartType}
            scorecardId={scorecardId}
            scorecardTypeId={scorecardTypeId}
            sourceScorecardIds= {sourceScorecardIds}
            chartOptions={chartOptions}
            dashboardType={props.dashboardSectionType}
            dashboardLevelOnClick={(label: string, occupationalLevelIds?: number[]) => {
              if (props.onClick) props.onClick(chartType, label, occupationalLevelIds) ;
            }}
          />
        );

      if (columnWidthTotalForRow + columnWidth > 12 && props.dashboardSectionType != DashboardSectionType.wsp) {
        columnWidthTotalForRow = 0;
        chartListGroups.push([]);
        groupCount++;
      }

      columnWidthTotalForRow += columnWidth;
      chartListGroups[groupCount].push({
        chartType: chartType,
        title: getFriendlyNameForChartType(chartType, props.scorecardTypeId, props.scorecardYear),
        colWidth: `col-lg-${columnWidth} col-md-${columnWidth}`,
        chartName: ChartType[chartType].toString(),
        component,
      });
    }

    return chartListGroups;
  }

  if (!!props.isVisible === false) {
    return <MDBox className='dashboard'></MDBox>
  }

  if (props.scorecardId === null) {
    return (
      <MDBox className="dashboard">
        <SmallLoading />
      </MDBox>
    )
  }
  return (
    <MDBox>
      {renderCharts(props).map(
        (column: Array<IChartComponentData>, columnIndex: number) => {

          return (
            <MDBox className="row row-eq-height" key={columnIndex}>

              {column.map((row: IChartComponentData, rowIndex: number) => {

                return (
                  <MDBox
                    key={`${row.chartName}-${columnIndex}-${rowIndex}`}
                    className={classNames(row.colWidth, "grid")}
                    debug-chart-name={row.chartName}
                    sx={{ p: 1 }}
                  >
                    <MDBox sx={{ background: 'white', borderRadius: '16px' }}>
                      {isEditing &&
                        row.chartType !== ChartType.AddNew && (
                          <MDBox className="action-container">
                            <IconButton
                              aria-label="close" size="small"
                              style={{ float: 'right', color: 'inherit' }}
                              onClick={() => deleteChart(row.chartType)}>
                              <DeleteIcon fontSize="small" sx={{color: 'red'}} />
                            </IconButton>
                          </MDBox>
                        )}
                      <MDTypography ml={1} mt={1} variant="h4" >{row.title ?? "Unknown"}</MDTypography>
                      {row.component}
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          );
        }
      )}
    </MDBox>
  )
}

